import log from 'loglevel';

// Browser console is quiet by default: only `warn` and `error` are shown.
//
// To enable verbose logging while debugging, set REACT_APP_LOG_LEVEL in your
// frontend .env.local (or shell) to one of: trace, debug, info, warn, error,
// silent. Example:
//
//   REACT_APP_LOG_LEVEL=debug npm run dev
//
// `test` runs always go silent so test output isn't polluted with app logs.
const defaultLevel = process.env.NODE_ENV === 'test' ? 'silent' : 'warn';

log.setLevel(process.env.REACT_APP_LOG_LEVEL || defaultLevel);

// Create a wrapper around loglevel to add context to log messages
const logger = {
    trace: (message, data) => {
        if (data) {
            log.trace(`${message}`, data);
        } else {
            log.trace(message);
        }
    },
    debug: (message, data) => {
        if (data) {
            log.debug(`${message}`, data);
        } else {
            log.debug(message);
        }
    },
    info: (message, data) => {
        if (data) {
            log.info(`${message}`, data);
        } else {
            log.info(message);
        }
    },
    warn: (message, data) => {
        if (data) {
            log.warn(`${message}`, data);
        } else {
            log.warn(message);
        }
    },
    error: (message, error) => {
        if (error) {
            // Extract useful information from the error
            const errorInfo = {
                message: error.message,
                stack: error.stack,
                ...(error.response && {
                    status: error.response.status,
                    statusText: error.response.statusText,
                    data: error.response.data,
                }),
            };
            log.error(`${message}`, errorInfo);
        } else {
            log.error(message);
        }
    },
};

// Create a wrapper function that automatically includes file information
const createLogger = (filename) => {
    return {
        trace: (message, data) => {
            const logMessage = `[${filename}] ${message}`;
            if (data) {
                log.trace(logMessage, data);
            } else {
                log.trace(logMessage);
            }
        },
        debug: (message, data) => {
            const logMessage = `[${filename}] ${message}`;
            if (data) {
                log.debug(logMessage, data);
            } else {
                log.debug(logMessage);
            }
        },
        info: (message, data) => {
            const logMessage = `[${filename}] ${message}`;
            if (data) {
                log.info(logMessage, data);
            } else {
                log.info(logMessage);
            }
        },
        warn: (message, data) => {
            const logMessage = `[${filename}] ${message}`;
            if (data) {
                log.warn(logMessage, data);
            } else {
                log.warn(logMessage);
            }
        },
        error: (message, error) => {
            const logMessage = `[${filename}] ${message}`;
            if (error) {
                // Extract useful information from the error
                const errorInfo = {
                    message: error.message,
                    stack: error.stack,
                    ...(error.response && {
                        status: error.response.status,
                        statusText: error.response.statusText,
                        data: error.response.data,
                    }),
                };
                log.error(logMessage, errorInfo);
            } else {
                log.error(logMessage);
            }
        },
    };
};

// Add a method to enable persistent logging to localStorage if needed
logger.enablePersistence = () => {
    const originalFactory = log.methodFactory;
    log.methodFactory = function (methodName, logLevel, loggerName) {
        const rawMethod = originalFactory(methodName, logLevel, loggerName);

        return function (message, data) {
            rawMethod(message, data);

            try {
                // Store logs in localStorage with timestamp
                const logs = JSON.parse(localStorage.getItem('application_logs') || '[]');
                logs.push({
                    timestamp: new Date().toISOString(),
                    level: methodName,
                    message,
                    data,
                });

                // Keep only the last 100 logs to avoid localStorage limits
                if (logs.length > 100) {
                    logs.shift();
                }

                localStorage.setItem('application_logs', JSON.stringify(logs));
            } catch (e) {
                // Silently fail if localStorage is not available
            }
        };
    };

    // Apply the new factory to all existing loggers
    log.setLevel(log.getLevel());
};

// Export both the default logger and the createLogger function
export default logger;
export { createLogger };
